import { IAttachmentPopulated } from 'bg-server/src/components/api/v1/attachment/types';
import { IExpedient } from 'bg-server/src/components/api/v1/expedient/types';
import { ITramitPopulated } from 'bg-server/src/components/api/v1/tramit/types';

import { ENV } from 'src/config/environments';
import { httpRequest } from 'src/helpers/http';
import { IUpdateAttachmentRequest } from 'src/interfaces';

class AttachmentService {
    private getApiEndpoint(): string {
        const selectedCity = localStorage.getItem('selectedCity') || ENV.apiEndpoints[0].key;
        const apiEndpoint = ENV.apiEndpoints.find(endpoint => endpoint.key === selectedCity);
        return apiEndpoint ? `${apiEndpoint.url}` : `${ENV.apiEndpoints[0].url}`;
    }

    // *** Expedient ATTACHMENTS ***
    public async getExpedientAttachment(expedientId: string, attachmentId: string): Promise<Blob> {
        const response = await httpRequest(`${this.getApiEndpoint()}/expedient/${expedientId}/attachment/${attachmentId}/file`, 'GET');

        if (!response.ok) {
            const json = await response.json();
            if (json.error !== undefined) {
                await Promise.reject(json.message);
            } else {
                await Promise.reject(response.statusText);
            }
        }
        const blob = await response.blob();
        return blob;
    }

    public async getTramiAttachmentObject(tramitId: string, attachmentId: string): Promise<IAttachmentPopulated> {
        const response = await httpRequest(`${this.getApiEndpoint()}/tramit/${tramitId}/attachment/${attachmentId}/file`, 'GET');
        const json = await response.json();
        if (json.error !== undefined) {
            await Promise.reject(json.message);
        }
        return json;
    }

    public async uploadExpedientAttachmentByDocNumber(file: File, expedientId: string, attachmentType: string): Promise<IExpedient> {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('originalname', file.name);

        return new Promise((resolve, reject) => {
            const xhr = new XMLHttpRequest();

            xhr.onload = () => {
                if (xhr.status === 201 || xhr.status === 200) {
                    resolve(JSON.parse(xhr.response));
                } else {
                    reject(JSON.parse(xhr.response) || xhr.statusText);
                }
            };

            xhr.onerror = () => reject(JSON.parse(xhr.response) || xhr.statusText);

            xhr.open('POST', `${this.getApiEndpoint()}/expedient/${expedientId}/attachment/${attachmentType}`, true);
            xhr.withCredentials = true;
            xhr.send(formData);
        });
    }

    public async uploadExpedientAttachmentById(file: File, expedientId: string, attachmentId: string): Promise<IExpedient> {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('originalname', file.name);

        return new Promise((resolve, reject) => {
            const xhr = new XMLHttpRequest();

            xhr.onload = () => {
                if (xhr.status === 201 || xhr.status === 200) {
                    resolve(JSON.parse(xhr.response));
                } else {
                    reject(JSON.parse(xhr.response) || xhr.statusText);
                }
            };

            xhr.onerror = () => reject(JSON.parse(xhr.response) || xhr.statusText);
            xhr.open('PUT', `${this.getApiEndpoint()}/expedient/${expedientId}/attachment/byid/${attachmentId}`, true);
            xhr.withCredentials = true;
            xhr.send(formData);
        });
    }

    public async manualSignExpedientAttachment(file: File, expedientId: string, attachmentId: string): Promise<IExpedient> {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('originalname', file.name);

        return new Promise((resolve, reject) => {
            const xhr = new XMLHttpRequest();

            xhr.onload = () => {
                if (xhr.status === 201 || xhr.status === 200) {
                    resolve(JSON.parse(xhr.response));
                } else {
                    reject(JSON.parse(xhr.response) || xhr.statusText);
                }
            };

            xhr.onerror = () => reject(JSON.parse(xhr.response) || xhr.statusText);

            xhr.open('PUT', `${this.getApiEndpoint()}/expedient/${expedientId}/attachment/signed/${attachmentId}`, true);
            xhr.withCredentials = true;
            xhr.send(formData);
        });
    }

    public async manualSignExpedientAttachmentByAjuntament(file: File, expedientId: string, attachmentId: string): Promise<IExpedient> {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('originalname', file.name);

        return new Promise((resolve, reject) => {
            const xhr = new XMLHttpRequest();

            xhr.onload = () => {
                if (xhr.status === 201 || xhr.status === 200) {
                    resolve(JSON.parse(xhr.response));
                } else {
                    reject(JSON.parse(xhr.response) || xhr.statusText);
                }
            };

            xhr.onerror = () => reject(JSON.parse(xhr.response) || xhr.statusText);

            xhr.open('PUT', `${this.getApiEndpoint()}/expedient/${expedientId}/attachment/signed-by-ajuntament/${attachmentId}`, true);
            xhr.withCredentials = true;
            xhr.send(formData);
        });
    }

    public async deleteExpedientAttachment(expedientId: string, attachmentId: string): Promise<IExpedient> {
        const response = await httpRequest(`${this.getApiEndpoint()}/expedient/${expedientId}/attachment/${attachmentId}`, 'DELETE');
        const json = await response.json();
        if (json.error !== undefined) {
            await Promise.reject(json.message);
        }
        return json;
    }

    public async updateExpedientAttachment(expedientId: string, attachmentId: string, body: IUpdateAttachmentRequest): Promise<IExpedient> {
        const response = await httpRequest(`${this.getApiEndpoint()}/expedient/${expedientId}/attachment/${attachmentId}`, 'PUT', body);
        const json = await response.json();
        if (json.error !== undefined) {
            await Promise.reject(json.message);
        }
        return json;
    }

    // *** TRAMIT ATTACHMENTS ***
    public async getTramitAttachment(tramitId: string, attachmentId: string): Promise<Blob> {
        const response = await httpRequest(`${this.getApiEndpoint()}/tramit/${tramitId}/attachment/${attachmentId}/file`, 'GET');

        if (!response.ok) {
            const json = await response.json();
            if (json.error !== undefined) {
                await Promise.reject(json.message);
            } else {
                await Promise.reject(response.statusText);
            }
        }

        const blob = await response.blob();
        return blob;
    }

    public async uploadTramitAttachmentByDocNumber(file: File, tramitId: string, attachmentType: string): Promise<{ tramit: ITramitPopulated, expedient: IExpedient }> {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('originalname', file.name);

        return new Promise((resolve, reject) => {
            const xhr = new XMLHttpRequest();

            xhr.onload = () => {
                if (xhr.status === 201 || xhr.status === 200) {
                    resolve(JSON.parse(xhr.response));
                } else {
                    reject(JSON.parse(xhr.response) || xhr.statusText);
                }
            };

            xhr.onerror = () => reject(JSON.parse(xhr.response) || xhr.statusText);

            xhr.open('POST', `${this.getApiEndpoint()}/tramit/${tramitId}/attachment/${attachmentType}`, true);
            xhr.withCredentials = true;
            xhr.send(formData);
        });
    }

    public async uploadTramitAttachmentById(file: File, tramitId: string, attachmentId: string): Promise<{ tramit: ITramitPopulated, expedient: IExpedient }> {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('originalname', file.name);

        return new Promise((resolve, reject) => {
            const xhr = new XMLHttpRequest();

            xhr.onload = () => {
                if (xhr.status === 201 || xhr.status === 200) {
                    resolve(JSON.parse(xhr.response));
                } else {
                    reject(JSON.parse(xhr.response) || xhr.statusText);
                }
            };

            xhr.onerror = () => reject(JSON.parse(xhr.response) || xhr.statusText);
            xhr.open('PUT', `${this.getApiEndpoint()}/tramit/${tramitId}/attachment/byid/${attachmentId}`, true);
            xhr.withCredentials = true;
            xhr.send(formData);
        });
    }

    public async deleteTramitAttachment(tramitId: string, attachmentId: string): Promise<{ tramit: ITramitPopulated, expedient: IExpedient }> {
        const response = await httpRequest(`${this.getApiEndpoint()}/tramit/${tramitId}/attachment/${attachmentId}`, 'DELETE');
        const json = await response.json();
        if (json.error !== undefined) {
            await Promise.reject(json.message);
        }
        return json;
    }

    /** Elimina el attachment completamente si su isForceDeletable=true */
    public async forceDeleteTramitAttachment(tramitId: string, attachmentId: string): Promise<{ tramit: ITramitPopulated, expedient: IExpedient }> {
        const response = await httpRequest(`${this.getApiEndpoint()}/tramit/${tramitId}/attachment/force-delete/${attachmentId}`, 'DELETE');
        const json = await response.json();
        if (json.error !== undefined) {
            await Promise.reject(json.message);
        }
        return json;
    }

    public async updateTramitAttachment(tramitId: string, attachmentId: string, body: IUpdateAttachmentRequest): Promise<{ tramit: ITramitPopulated, expedient: IExpedient }> {
        const response = await httpRequest(`${this.getApiEndpoint()}/tramit/${tramitId}/attachment/${attachmentId}`, 'PUT', body);
        const json = await response.json();
        if (json.error !== undefined) {
            await Promise.reject(json.message);
        }
        return json;
    }

    public async signTramitAttachment(tramitId: string, attachmentId: string): Promise<string> {
        const response = await httpRequest(`${this.getApiEndpoint()}/tramit/${tramitId}/attachment/${attachmentId}/signrequest`, 'GET');
        const json = await response.json();
        if (json.error !== undefined) {
            await Promise.reject(json.message);
        }
        return json.url;
    }

    public async manualSignTramitAttachment(file: File, tramitId: string, attachmentId: string): Promise<{ tramit: ITramitPopulated, expedient: IExpedient }> {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('originalname', file.name);

        return new Promise((resolve, reject) => {
            const xhr = new XMLHttpRequest();

            xhr.onload = () => {
                if (xhr.status === 201 || xhr.status === 200) {
                    resolve(JSON.parse(xhr.response));
                } else {
                    reject(JSON.parse(xhr.response) || xhr.statusText);
                }
            };

            xhr.onerror = () => reject(JSON.parse(xhr.response) || xhr.statusText);

            xhr.open('PUT', `${this.getApiEndpoint()}/tramit/${tramitId}/attachment/signed/${attachmentId}`, true);
            xhr.withCredentials = true;
            xhr.send(formData);
        });
    }

    public async manualSignTramitAttachmentByAjuntament(file: File, tramitId: string, attachmentId: string): Promise<{ tramit: ITramitPopulated, expedient: IExpedient }> {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('originalname', file.name);

        return new Promise((resolve, reject) => {
            const xhr = new XMLHttpRequest();

            xhr.onload = () => {
                if (xhr.status === 201 || xhr.status === 200) {
                    resolve(JSON.parse(xhr.response));
                } else {
                    reject(JSON.parse(xhr.response) || xhr.statusText);
                }
            };

            xhr.onerror = () => reject(JSON.parse(xhr.response) || xhr.statusText);

            xhr.open('PUT', `${this.getApiEndpoint()}/tramit/${tramitId}/attachment/signed-by-ajuntament/${attachmentId}`, true);
            xhr.withCredentials = true;
            xhr.send(formData);
        });
    }

    public async generateAutocompleteDocument(idExpedient: string, docType: string): Promise<Blob> {
        const response = await httpRequest(`${this.getApiEndpoint()}/expedient/${idExpedient}/docgenerate/${docType}`, 'GET');

        if (!response.ok) {
            const json = await response.json();
            if (json.error !== undefined) {
                await Promise.reject(json.message);
            } else {
                await Promise.reject(response.statusText);
            }
        }

        const blob = await response.blob();
        return blob;
    }
}

export const attachmentService = new AttachmentService();
